import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box } from "@mui/material";

import { Layout } from "Layout";

import { NEW_RECORD_SKELETON } from "appConfig";

import { EditModalHeader } from "components/EditModalHeader";
import { FrmFieldGrid } from "components/FrmFieldGrid";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { FrmResizeTextField } from "components/formFields/FrmResizeTextField";
import { FrmTextField } from "components/formFields/FrmTextField";
import { FrmTextFieldWithHoverHelp } from "components/formFields/FrmTextFieldWithHoverHelp";
import { FrmBarcode } from "components/formFields/frm/FrmBarcode";
import { FrmLocation } from "components/formFields/frm/FrmLocation";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";

import { i18n } from "services/i18nService";
import { getRecord } from "services/sosInventoryService/sosApi";
import { isPersistedRecord } from "services/utility/misc";

import { useErrors } from "hooks/useErrors";
import { useLocations } from "hooks/useLocations";
import { useRecord } from "hooks/useRecord";

import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

export function Bin(props) {
  const { objectType, id, onClose } = props;

  const dispatch = useDispatch();

  const [record, setRecord, handleInputFieldChange] = useRecord();
  const { errors, setErrors, isInError } = useErrors();

  const binTrackedLocations = useLocations(true);

  const loadingState = useSelector((state) => state.loading.editModal);

  useEffect(() => {
    async function _getRecord() {
      if (id) {
        dispatch(editModalLoadingIndicatorOn());
        const response = await getRecord(objectType, id);
        setRecord(response);
        dispatch(editModalLoadingIndicatorOff());
      } else {
        const emptyRecord = NEW_RECORD_SKELETON[objectType]();
        setRecord(emptyRecord);
      }
    }
    _getRecord();
  }, [setRecord, id, objectType, dispatch]);

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        onSaveAndKeepEditing={setRecord}
        objectType={objectType}
        setErrors={setErrors}
        text={record?.name || ""}
        handleClose={onClose}
      />

      {loadingState && <Loading />}

      {record && (
        <ObjectEditDialogContent>
          <Layout
            pageTitle={
              id
                ? `${i18n(`objectType.${objectType}.Sentence`)} ${
                    record?.name || ""
                  }`
                : i18n(`objectType.${objectType}.New`)
            }
          >
            <Box flexGrow="1" position="relative" m={2} mb={0}>
              <FormErrors errors={errors} setErrors={setErrors} />
              {record ? (
                <Box>
                  <FrmFieldGrid gridAutoFlow="dense">
                    <div style={{ gridColumn: "1 / 2", maxWidth: "24rem" }}>
                      <FrmTextField
                        name="number"
                        value={record.number}
                        label={i18n("frmLabel.BinNumber")}
                        onValueBlur={handleInputFieldChange}
                        error={isInError("number")}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2", maxWidth: "24rem" }}>
                      <FrmBarcode number={record.number} />
                    </div>
                    <div style={{ gridColumn: "1 / 2" }}>
                      <FrmFieldGrid gridAutoFlow="dense">
                        <FrmResizeTextField
                          name="description"
                          value={record.description}
                          label={i18n("frmLabel.Description")}
                          minRows={2}
                          width="100%"
                          onValueBlur={handleInputFieldChange}
                        />
                      </FrmFieldGrid>
                    </div>
                    <div style={{ gridColumn: "1 / 2" }}>
                      <FrmLocation
                        width="25rem"
                        onlyBinTracked
                        value={record.location}
                        locations={binTrackedLocations}
                        onValueChange={handleInputFieldChange}
                        disabled={isPersistedRecord(record.id)}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / 2", maxWidth: "8rem" }}>
                      <FrmTextFieldWithHoverHelp
                        number
                        name="sortOrder"
                        label={i18n("frmLabel.SequenceNumber")}
                        onValueBlur={handleInputFieldChange}
                        value={record.sortOrder}
                        helpText={i18n("hoverHelp.SequenceNumber")}
                        error={isInError("sortOrder")}
                      />
                    </div>
                  </FrmFieldGrid>
                  <VSpace space={1} />
                  <FrmFieldGrid gridAutoFlow="dense"></FrmFieldGrid>
                </Box>
              ) : (
                !isInError("record") && (
                  <FixedLoadingIndicator
                    text={`${i18n("global.Loading")}...`}
                  />
                )
              )}
            </Box>
            <VSpace space={4} />
          </Layout>
        </ObjectEditDialogContent>
      )}
    </>
  );
}
